import { createRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { IconDownload } from '@tabler/icons-react';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import {
  AUDIT_ACTIONS,
  downloadAuditCsv,
  useAuditLog,
  type AuditAction,
  type AuditFilters,
} from '../../../../hooks/useAuditLog.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';
import { Select } from '../../../../components/ui/Select.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/audit-log',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'admin' && role !== 'super_admin') throw redirect({ to: '/app' });
  },
  component: AuditLogPage,
});

const PAGE_SIZE = 50;

const ACTION_LABELS: Record<AuditAction, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  share_toggle: 'Share toggle',
  send: 'Send',
  login: 'Login',
  invite_accepted: 'Invite accepted',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function AuditLogPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isSuper = role === 'super_admin';

  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState<'' | AuditAction>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const filters: AuditFilters = useMemo(
    () => ({
      actorId: actorId || undefined,
      action: action || undefined,
      from: from || undefined,
      to: to || undefined,
      q: q.trim() || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [actorId, action, from, to, q, page],
  );

  const query = useAuditLog(filters);
  const data = query.data;
  const total = data?.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, (page + 1) * PAGE_SIZE);

  function resetToFirstPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadAuditCsv(filters);
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div>
      <AdminPageHeader
        title="Audit log"
        description={
          isSuper
            ? 'Every privileged action across all agencies.'
            : 'Every privileged action in your agency workspace.'
        }
        actions={
          <Button
            variant="secondary"
            leftIcon={<IconDownload size={16} />}
            loading={exporting}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        }
      />

      {exportError && (
        <p className="mb-4 text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3 text-sm">
          Export failed: {exportError}
        </p>
      )}

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Select
          value={actorId}
          onChange={(e) => resetToFirstPage(setActorId)(e.target.value)}
          aria-label="Filter by actor"
        >
          <option value="">All actors</option>
          {data?.actors.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>

        <Select
          value={action}
          onChange={(e) => resetToFirstPage(setAction)(e.target.value as '' | AuditAction)}
          aria-label="Filter by action"
        >
          <option value="">All actions</option>
          {AUDIT_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABELS[a]}
            </option>
          ))}
        </Select>

        <Input
          type="date"
          value={from}
          onChange={(e) => resetToFirstPage(setFrom)(e.target.value)}
          aria-label="From date"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => resetToFirstPage(setTo)(e.target.value)}
          aria-label="To date"
        />
        <Input
          type="search"
          placeholder="Search…"
          value={q}
          onChange={(e) => resetToFirstPage(setQ)(e.target.value)}
          aria-label="Search audit log"
        />
      </div>

      {query.isLoading && <p className="text-muted">Loading…</p>}
      {query.error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load the audit log: {(query.error as Error).message}
        </p>
      )}

      {data && data.items.length === 0 && (
        <div className="border border-dashed border-line-strong rounded-xl p-10 text-center bg-white/40">
          <p className="text-ink-soft font-medium">No matching events.</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">When</th>
                  <th className="px-5 py-3 font-medium">Actor</th>
                  <th className="px-5 py-3 font-medium">Action</th>
                  <th className="px-5 py-3 font-medium">Resource</th>
                  {isSuper && <th className="px-5 py-3 font-medium">Agency</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.items.map((row) => (
                  <tr key={row.id} className="hover:bg-cream/30 transition-colors align-top">
                    <td className="px-5 py-3 text-muted text-xs whitespace-nowrap">
                      {formatDateTime(row.occurred_at)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-ink">{row.actor_name ?? 'System'}</div>
                      {row.actor_email && (
                        <div className="text-muted text-xs">{row.actor_email}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center rounded-full border border-line bg-cream-deep/50 px-2.5 py-0.5 text-xs font-medium text-navy">
                        {ACTION_LABELS[row.action] ?? row.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-ink-soft">
                      <div>{row.resource_type}</div>
                      {row.summary && (
                        <div className="text-ink text-xs mt-0.5">{row.summary}</div>
                      )}
                      {row.resource_id && (
                        <div className="text-muted-soft text-xs font-mono">{row.resource_id}</div>
                      )}
                    </td>
                    {isSuper && (
                      <td className="px-5 py-3 text-ink-soft">{row.tenant_name ?? '—'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-muted">
            <span>
              Showing {showingFrom}–{showingTo} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={showingTo >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
